import React, { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Clock, Lightbulb } from '@phosphor-icons/react';
import { setPageMeta, breadcrumbLd } from '../utils/seo';
import { getTutorial, TUTORIALS, ROLES } from '../data/tutorials';

const roleMeta = (key) => ROLES.find((r) => r.key === key) || ROLES[3];

export default function TutorialDetail() {
  const { slug } = useParams();
  const tutorial = getTutorial(slug);

  useEffect(() => {
    if (!tutorial) return;
    setPageMeta({
      title: `${tutorial.title} | ConstructHelp Tutorials`,
      description: tutorial.summary,
      url: `${window.location.origin}/tutorials/${tutorial.slug}`,
      jsonLd: [
        {
          '@context': 'https://schema.org',
          '@type': 'HowTo',
          name: tutorial.title,
          description: tutorial.summary,
          step: tutorial.steps.map((s, i) => ({
            '@type': 'HowToStep',
            position: i + 1,
            name: s.title,
            text: s.body,
          })),
        },
        breadcrumbLd([
          { name: 'Home', url: window.location.origin },
          { name: 'Tutorials', url: `${window.location.origin}/tutorials` },
          { name: tutorial.title },
        ]),
      ],
    });
    window.scrollTo(0, 0);
  }, [tutorial]);

  if (!tutorial) {
    return (
      <div className="min-h-screen bg-[#E8E9EB] dark:bg-[#121212] flex items-center justify-center">
        <div className="text-center">
          <p className="font-black text-xl text-neutral-900 dark:text-white mb-4">Tutorial not found</p>
          <Link to="/tutorials" className="text-[#D95800] font-bold underline">Back to tutorials</Link>
        </div>
      </div>
    );
  }

  const rm = roleMeta(tutorial.role);
  const related = TUTORIALS.filter((t) => t.slug !== slug && (t.role === tutorial.role || t.role === 'everyone')).slice(0, 3);

  return (
    <div className="min-h-screen bg-[#E8E9EB] dark:bg-[#121212]" data-testid="tutorial-detail-page">
      {/* Hero */}
      <div className="bg-[#0F172A] border-b-4 border-[#D95800]">
        <div className="max-w-3xl mx-auto px-4 py-10">
          <Link to="/tutorials" className="inline-flex items-center gap-1.5 text-neutral-400 hover:text-white text-xs font-bold uppercase tracking-wider mb-4 transition-colors" data-testid="back-to-tutorials-link">
            <ArrowLeft size={14} weight="bold" /> All Tutorials
          </Link>
          <div className="flex items-center gap-2 mb-3">
            <span className={`${rm.color} text-[10px] font-black uppercase tracking-widest text-white px-2 py-0.5 border border-black`}>{rm.label}</span>
            <span className="text-[11px] text-neutral-400 flex items-center gap-1">
              <Clock size={12} weight="bold" /> {tutorial.minutes} min read
            </span>
          </div>
          <h1 className="text-2xl md:text-4xl font-black uppercase tracking-tight text-white leading-tight" data-testid="tutorial-title">
            {tutorial.title}
          </h1>
          <p className="text-neutral-400 mt-3 text-sm md:text-base">{tutorial.summary}</p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-8">
        {/* Steps */}
        <ol className="space-y-4" data-testid="tutorial-steps">
          {tutorial.steps.map((s, i) => (
            <li key={i} className="bg-white dark:bg-neutral-800 border-2 border-black rounded-xl p-5 shadow-[4px_4px_0px_#000] flex gap-4" data-testid={`tutorial-step-${i + 1}`}>
              <span className="shrink-0 w-9 h-9 flex items-center justify-center bg-[#D95800] text-white font-black border-2 border-black rounded-lg">{i + 1}</span>
              <div>
                <h2 className="font-black text-neutral-900 dark:text-white leading-snug">{s.title}</h2>
                <p className="text-sm text-neutral-600 dark:text-neutral-400 mt-1.5 leading-relaxed">{s.body}</p>
              </div>
            </li>
          ))}
        </ol>

        {/* Tips */}
        {tutorial.tips?.length > 0 && (
          <div className="bg-amber-50 dark:bg-amber-900/20 border-2 border-amber-500 rounded-xl p-5 mt-8" data-testid="tutorial-tips">
            <p className="font-black uppercase text-sm text-amber-800 dark:text-amber-300 flex items-center gap-2 mb-2">
              <Lightbulb size={16} weight="fill" /> Pro Tips
            </p>
            <ul className="space-y-1.5">
              {tutorial.tips.map((tip, i) => (
                <li key={i} className="text-sm text-amber-900 dark:text-amber-200 flex gap-2">
                  <span className="shrink-0">•</span> {tip}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* CTA */}
        <div className="bg-[#0F172A] border-2 border-black rounded-xl p-6 shadow-[4px_4px_0px_#D95800] my-8" data-testid="tutorial-cta">
          <p className="text-white font-black uppercase text-lg">Try it yourself</p>
          <p className="text-neutral-400 text-sm mt-1">The fastest way to learn is to jump in — your account is free.</p>
          <div className="flex flex-wrap gap-2 mt-4">
            <Link to="/signup" className="bg-[#D95800] text-white font-black uppercase text-xs px-5 py-2.5 border-2 border-black">Get Started Free</Link>
            <Link to="/tutorials" className="bg-white text-neutral-900 font-black uppercase text-xs px-5 py-2.5 border-2 border-black">More Tutorials</Link>
          </div>
        </div>

        {/* Related */}
        {related.length > 0 && (
          <div data-testid="tutorial-related">
            <h2 className="text-lg font-black uppercase text-neutral-900 dark:text-white mb-3">Keep Learning</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {related.map((r) => (
                <Link key={r.slug} to={`/tutorials/${r.slug}`} className="group bg-white dark:bg-neutral-800 border-2 border-black rounded-xl p-4 hover:-translate-y-0.5 transition-transform">
                  <span className={`${roleMeta(r.role).color} text-white text-[9px] font-black uppercase px-1.5 py-0.5 border border-black rounded`}>{roleMeta(r.role).label}</span>
                  <p className="font-bold text-sm text-neutral-900 dark:text-white mt-2 leading-snug group-hover:text-[#D95800] transition-colors">{r.title}</p>
                  <span className="inline-flex items-center gap-1 text-[10px] font-black uppercase text-[#D95800] mt-2">Read <ArrowRight size={11} weight="bold" /></span>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
